<template>
  <div class="ma-4">
    <v-row>
      <v-dialog
        v-model="showArticleModal"
        fullscreen
        persistent
        :loading="loading"
        hide-overlay
        transition="dialog-bottom-transition"
        :retain-focus="false"
      >
        <v-card>
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="resetForm">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>Article Details</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn
                dark
                text
                @click="validateArticleForm"
                :disabled="saving"
                :loading="saving"
              >Update</v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-text-field
                      label="Title*"
                      v-model="article_data.title"
                      required
                      :rules="titleRules"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6">
                    <v-text-field
                      label="Short Title*"
                      v-model="article_data.short_title"
                      required
                      :rules="shortTitleRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-text-field
                      label="Author*"
                      v-model="article_data.author"
                      required
                      :rules="authorRules"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field
                      placeholder="HOT!"
                      label="Caption/Label"
                      v-model="article_data.tag_text"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-autocomplete
                      v-model="article_data.tag_type"
                      label="Sport"
                      required
                      :items="tag_types"
                      :rules="tagTypeRules"
                      @change="onChangeTagType"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="article_data.tag_name"
                      label="League"
                      multiple
                      required
                      :rules="tagNameRules"
                      :items="tag_name_options"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-text-field
                      label="Article URL Slug"
                      v-model="article_data.slug"
                      autofocus
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="1" v-if="article_data.title">
                    <v-btn x-small color="success" class="mb-1" @click="generateSlug">
                      <pulse-loader v-if="generatingSlug"></pulse-loader>
                      <span v-else>generate slug</span>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="4" md="2" class="d-flex">
                    <v-switch v-model="article_data.is_trending" label="Trending"></v-switch>
                  </v-col>
                  <v-col cols="12" sm="4" v-show="article_data.is_trending">
                    <v-autocomplete
                      clearable
                      v-model="article_data.us_states_id"
                      label="State"
                      :items="states_option"
                      :disabled="!article_data.is_trending"
                      multiple
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" md="3" v-show="article_data.is_trending">
                    <v-menu
                      ref="trendingMenu"
                      v-model="trendingMenu"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      :close-on-content-click="false"
                      :return-value.sync="article_data.trending_expiration_date"
                      :disabled="!article_data.is_trending"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="article_data.trending_expiration_date"
                          label="Trending Expiration Date"
                          :prepend-icon="icons.event"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="article_data.trending_expiration_date"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="trendingMenu = false">Cancel</v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.trendingMenu.save(article_data.trending_expiration_date)"
                        >OK</v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="12" sm="6" md="3">
                    <v-menu
                      ref="menu"
                      v-model="menu"
                      :close-on-content-click="false"
                      :return-value.sync="published_date"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="published_date"
                          label="Publish Date"
                          :prepend-icon="icons.event"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="published_date" no-title scrollable>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
                        <v-btn text color="primary" @click="$refs.menu.save(published_date)">OK</v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-menu
                      ref="timeMenu"
                      v-model="timeMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      :return-value.sync="time"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="time"
                          label="Time"
                          :prepend-icon="icons.clock"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :clearable="false"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        min="01:01"
                        format="24hr"
                        v-if="timeMenu"
                        v-model="time"
                        full-width
                        @click:minute="$refs.timeMenu.save(time)"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6" md="1">
                    <v-btn x-small color="success" @click="getDateNow" class="mb-1">Now</v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-textarea
                      label="Summary*"
                      v-model="article_data.summarized_description"
                      required
                      :rules="summaryRules"
                      solo
                      counter="500"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="4" md="4">
                    <div v-if="article_data.thumbnail">
                      <label for>Article Image</label>
                      <v-img
                        :src="article_data.thumbnail.url"
                        contain
                        max-width="500"
                        max-height="300"
                      ></v-img>
                    </div>
                    <v-file-input
                      clearable
                      show-size
                      label="Change Article Image"
                      required
                      accept=".jpg, .jpeg, .png"
                      v-model="new_thumbnail"
                    ></v-file-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="12">
                    <small
                      class="article-content-error"
                      v-if="article_content_errors"
                    >{{ article_content_errors }}</small>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" class="mx-2" @click="updateEditorKey += 1">
                          <v-icon small dark>mdi-refresh</v-icon>
                        </v-btn>
                      </template>
                      <span>Reload Editor</span>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="12" sm="12" md="12">
                    <h4>Article Content</h4>
                    <editor
                      :key="updateEditorKey"
                      id="update-editor"
                      v-model="article_data.content_description"
                      api-key="owh5wfefwxc6ww1ieij5ne4rtzan6ycdfjtvpjhap6zv86zs"
                      :init="init"
                    />
                  </v-col>
                </v-row>
                <v-spacer></v-spacer>
                <v-row class="social-tags-header">
                    <v-col cols="12" md="12">
                      <span class="text-h4">Social Sharing Meta Tags</span>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        hint="There is no limit on the number of characters, but it’s best to stay between 60 and 90. If your title is longer than 100 characters, Facebook will truncate it to only 88!"
                        label="Title"
                        v-model="article_data.social_summary_title"
                      ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <v-textarea
                      hint="Keep it short and sweet. Facebook recommends 2–4 sentences, but that often truncates."
                      label="Description"
                      v-model="article_data.social_summary_description"
                      solo
                      counter="500"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="4" md="4">
                    <div v-if="article_data.socialThumbnail">
                      <label>Image</label>
                      <v-img
                        :src="article_data.socialThumbnail.url"
                        contain
                        max-width="500"
                        max-height="300"
                      ></v-img>
                    </div>
                    <v-file-input
                      clearable
                      show-size
                      label="Change Image"
                      required
                      accept=".jpg, .jpeg, .png"
                      v-model="new_social_thumbnail"
                    ></v-file-input>
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-card-text>
        </v-card>
        <v-overlay absolute :value="loading" :z-index="3">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </v-overlay>
        <v-overlay absolute :value="updating" :z-index="3">
          <v-dialog v-model="updating" hide-overlay persistent width="300">
            <v-card color="primary" dark>
              <v-card-text>
                {{ updating_message }}
                <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-overlay>
      </v-dialog>
    </v-row>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import map from 'lodash/map'
import head from 'lodash/head'
import get from 'lodash/get'
import { mdiCalendarMonth, mdiClockOutline } from '@mdi/js'
import editorConfig from '@/utils/articles/editor-config'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import dates from '@/utils/dates'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

dayjs.extend(utc)

export default {
  name: 'ArticleDetails',

  data () {
    return {
      generatingSlug: false,
      valid: true,
      loading: false,
      saving: false,
      trendingMenu: false,
      featuredArticleIndex: '',
      article_data: {
        title: '',
        author: '',
        content_description: '',
        facebook_url: '',
        instagram_url: '',
        twitter_url: '',
        is_trending: 0,
        trending_expiration_date: 0,
        tag_text: '',
        tag_name: [],
        tag_type: '',
        thumbnail: '',
        loader: null,
        city_id: '',
        us_states_id: [],
        short_title: '',
        summarized_description: '',
        featured_article: {
          order_index: ''
        },
        published_date: '',
        social_summary_title: '',
        social_summary_description: '',
        social_thumbnail: null,
        slug: ''
      },
      orderIndexItems: [
        {
          text: 'Main Feature',
          value: 1
        },
        {
          text: 'Sub Feature 2',
          value: 2
        },
        {
          text: 'Sub Feature 3',
          value: 3
        },
        {
          text: 'Sub Feature 4',
          value: 4
        },
        {
          text: 'Sub Feature 5',
          value: 5
        }
      ],
      tag_types: [
        {
          text: 'Baseball',
          value: 'baseball'
        },
        {
          text: 'Basketball',
          value: 'basketball'
        },
        {
          text: 'Football',
          value: 'football'
        },
        {
          text: 'Golf',
          value: 'golf'
        },
        {
          text: 'Hockey',
          value: 'hockey'
        },
        {
          text: 'Soccer',
          value: 'soccer'
        },
        {
          text: 'Tennis',
          value: 'tennis'
        }
      ],
      sport_categories: {},
      titleRules: [v => !!v || 'Title is required'],
      shortTitleRules: [v => !!v || 'Short Title is required'],
      summaryRules: [
        v => !!v || 'Article Summary is required',
        v => (v && v.length < 500) || 'Maximum of 500 characters'
      ],
      authorRules: [v => !!v || 'Author is required'],
      articleContentRules: [v => !!v || 'Article Content is required'],
      tagNameRules: [v => (v && v.length > 0) || 'League is required'],
      tagTypeRules: [v => !!v || 'Sport is required'],
      article_content_errors: '',
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      },
      stateRules: [
        v => !(this.article_data.is_trending === true) || 'State is required'
      ],
      showArticleModal: this.viewArticle,
      articleId: this.recordId,
      tag_name_options: [],
      new_thumbnail: null,
      new_social_thumbnail: null,
      updating: false,
      updating_message: '',
      update_thumbnail: false,
      is_trending: false,
      trending_expiration_date: '',
      is_featured: false,
      states_option: [],
      temp_cities_option: [],
      cities_option: [],
      updateEditorKey: 0,
      menu: false,
      timeMenu: false,
      time: null,
      published_date: '',
      seconds: '00',
      icons: {
        event: mdiCalendarMonth,
        clock: mdiClockOutline
      },
      init: editorConfig
    }
  },

  props: {
    viewArticle: {
      type: Boolean
    },
    recordId: {
      type: Number
    }
  },

  components: {
    PulseLoader
  },

  computed: {
    ...mapState({
      tags: state => state.tag.list,
      articleCreateAlert: state => state.article.articleCreateAlert,
      articleCreateStatus: state => state.article.articleCreateStatus,
      articleDetails: state => state.article.articleDetails,
      usStates: state => state.usStates.list
    })
  },

  methods: {
    ...mapActions({
      updateArticle: 'article/updateArticle',
      getArticleDetails: 'article/getArticleDetails',
      updateArticleThumbnail: 'article/updateArticleThumbnail',
      updateMainFeature: 'article/updateMainFeature',
      updateIsTrending: 'article/updateIsTrending',
      getTags: 'tag/getTags',
      getStates: 'usStates/getStates',
      updateSocialThumbnail: 'article/updateSocialThumbnail',
      generateArticleSlug: 'article/generateArticleSlug'
    }),

    async setTagData () {
      let params = {}
      let tagType = get(head(this.article_data.tags), 'type')

      if (tagType) {
        this.article_data.tag_type = tagType.toLowerCase()
        params.withType = tagType

        await this.getTags(params)

        this.tag_name_options = map(this.tags, 'name')
        this.article_data.tag_name = map(this.articleDetails.tags, 'name')
      }
    },

    async onChangeTagType () {
      let params = {}
      this.article_data.tag_name = []

      params.withType = this.article_data.tag_type

      this.loading = true
      await this.getTags(params)
        .then(() => {
          this.tag_name_options = map(this.tags, 'name')
        })
        .catch(err => {
          this.snackbar.color = 'danger'
          this.showSnackbar('Invalid category name')
        })
      this.loading = false
    },

    validateArticleForm () {
      var validateContent = this.validateArticleContent()
      if (this.$refs.form.validate() && validateContent) {
        this.updateSelectedArticle()
      }
    },

    validateArticleContent () {
      if (!this.article_data.content_description) {
        this.article_content_errors = 'Article Content is required.'
        return false
      } else {
        this.article_content_errors = ''
        return true
      }
    },

    async updateSelectedArticle () {
      this.updating = true
      this.updating_message = 'Updating Article Data'
      this.formatPublishedDate()

      await this.updateArticle(this.article_data)

      if (this.new_thumbnail) {
        let formData = new FormData()
        formData.append('thumbnail', this.new_thumbnail)
        formData.append('id', this.article_data.id)

        this.updating_message = 'Updating Article Thumbnail'
        await this.updateArticleThumbnail(formData)

        this.update_thumbnail = false
      }

      if (this.new_social_thumbnail) {
        let formData = new FormData()
        formData.append('social_thumbnail', this.new_social_thumbnail)
        formData.append('id', this.article_data.id)

        this.updating_message = 'Updating Article Social Image'
        await this.updateSocialThumbnail(formData)
      }

      if (this.featuredArticleIndex == 1) {
        await this.updateMainFeature({
          id: this.article_data.id,
          order_index: this.featuredArticleIndex
        })
      }

      if (this.article_data.is_trending === true && this.is_trending === false) {
        await this.updateIsTrending({ id: this.article_data.id })
      }

      this.updating = false
      this.snackbar.color = this.articleCreateStatus
      this.showSnackbar(this.articleCreateAlert)

      if (this.articleCreateStatus === 'success') {
        this.showArticleModal = false
        this.$emit('refreshArticleList')
        this.resetForm()
      }
    },

    showSnackbar (message) {
      this.snackbar.message = message
      this.snackbar.show = true
    },

    resetForm () {
      this.$refs.form.reset()
      this.article_data.content_description = ''
      this.article_data.tag_name = ''
      this.article_data.thumbnail = null
      this.showArticleModal = false
      this.updateEditorKey = 0
      this.$emit('closeArticleDetails')
      this.$emit('clearSelectedArticle')
    },

    async getArticleData () {
      this.loading = true
      let params = { id: this.articleId }
      await this.getArticleDetails(params)

      this.updateEditorKey += 1
      this.article_data = this.articleDetails
      this.is_trending = this.articleDetails.is_trending
      this.is_featured = this.articleDetails.is_featured

      if (this.article_data.trending_expiration_date) {
        this.article_data.trending_expiration_date = dayjs.utc(this.article_data.trending_expiration_date).format('YYYY-MM-DD')
      }

      if (this.articleDetails.featured_article) {
        this.featuredArticleIndex = this.articleDetails.featured_article.order_index
      }

      this.article_data.us_states_id = map(this.articleDetails.location, 'us_states_id')

      await this.setTagData()
      this.setDateTimePickerData()
      this.loading = false
    },

    async fetchUSStates (page = 1) {
      let params = { page }
      await this.getStates(params)
      let states = this.usStates

      states.forEach((value, key, map) => {
        this.states_option.push({
          text: value.name,
          value: value.id
        })
      })
    },

    getDateNow () {
      const today = dayjs()
      this.published_date = today.format('YYYY-MM-DD')
      this.time = today.format('HH:mm')
      this.seconds = today.format('ss')
    },

    formatPublishedDate () {
      let time = ''
      if (this.published_date) {
        if (this.time) {
          time = this.time + ':' + this.seconds
        } else {
          time = this.time = dayjs().format('HH:mm')
        }

        this.article_data.published_date = dates
          .localDateToUTC(this.published_date + ' ' + time)
          .format('YYYY-MM-DD HH:mm:ss')
      }
    },

    setDateTimePickerData () {
      if (this.article_data.published_date) {
        const publishedDate = new Date(this.article_data.published_date)
        this.published_date = publishedDate.toISOString().substr(0, 10)
        this.time = ('0' + publishedDate.getHours()).slice(-2) + ':' + ('0' + publishedDate.getMinutes()).slice(-2)
        this.seconds = ('0' + publishedDate.getSeconds()).slice(-2)
      }
    },

    async generateSlug () {
      this.generatingSlug = true
      let params = { title: this.article_data.title }

      await this.generateArticleSlug(params)
        .then(response => {
          this.article_data.slug = response.data.slug
        }).catch(err => {
          console.log(err)
        })

      this.generatingSlug = false
    },
  },

  watch: {
    viewArticle (val) {
      this.showArticleModal = val
    },
    published_date (val) {
      if (val) {
        this.formatPublishedDate()
      } else {
        this.time = null
      }
    },
    recordId (val) {
      this.articleId = val
      if (val !== 0) {
        this.getArticleData()
      }
    }
  },

  created () {
    this.fetchUSStates()
  }
}
</script>

<style scoped>
.article-content-error {
  color: var(--v-error-base) !important;
}
.social-tags-header {
  padding-top: 100px !important;
}
</style>
